/**
 * 图片上传器 - 独立模块
 * 版本: 1.0.0
 * 功能: 上传图片 -> 生成设计 -> 选择图片 -> 保存到购物车
 *
 * 依赖:
 * - guest-account.js (游客账号管理)
 * - 全局弹窗 HTML 结构
 * - message/message-toast.js (消息提示组件)
 */

// API base URL — live mode only (see AI_UPLOAD_CONFIG in theme)
const DEFAULT_BASE_URL =
  window.AI_UPLOAD_CONFIG?.apiBaseUrl ||
  window.THEME_CONFIG?.apiBaseUrl ||
  "";

class AiImageUploader {
  constructor(options = {}) {
    // 配置选项
    this.config = {
      // API 配置
      apiBaseUrl: options.apiBaseUrl || DEFAULT_BASE_URL,
      uploadEndpoint:
        options.uploadEndpoint || "/models/createModelsFromImages",
      pollEndpoint: options.pollEndpoint || "/teamCuteTasks/getTaskHistory",
      tokenStorageKey: options.tokenStorageKey || "deliver3d_guest_token",

      // 生成配置
      jobNum: options.jobNum || 2, // 生成图片数量
      sourceType: options.sourceType || "cute_single_img2img_deliver3d_aws",
      defaultStyle: options.defaultStyle || "Q-Style",

      // 轮询配置
      pollInterval: options.pollInterval || 2000, // 2秒
      maxPollAttempts: options.maxPollAttempts || 100, // 最多轮询100次

      // 文件限制
      maxFileSizeMB: options.maxFileSizeMB || 50,
      acceptFileTypes: options.acceptFileTypes || [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
        "image/WEBP",
        "image/heic",
        "image/HEIC",
      ],

      // 分辨率限制
      minDimension: options.minDimension || 200, // 最小宽度或高度
      maxDimension: options.maxDimension || 8096, // 最大宽度或高度

      // 回调函数
      onUploadStart: options.onUploadStart || null,
      onUploadProgress: options.onUploadProgress || null,
      onUploadSuccess: options.onUploadSuccess || null,
      onUploadError: options.onUploadError || null,
      onImageSelect: options.onImageSelect || null,
      onImageGenerated: options.onImageGenerated || null,
    };

    // 风格映射
    this.styleMapping = {
      Chibi: "Q-Style",
      Pixar: "Pixar-Style",
      Minecraft: "Minecraft-Style",
      Realistic: "Realistic-Style",
    };

    // 状态
    this.abortController = null;
    this.progressAnimation = null;
  }

  /**
   * Initialize uploader
   * @param {string|HTMLElement} triggerSelector - Upload button selector or element
   * @param {string|HTMLElement} inputSelector - File input selector or element
   * @param {string|HTMLElement} hiddenInputSelector - Hidden input selector or element (saves image URL)
   */
  init(triggerSelector, inputSelector, hiddenInputSelector) {
    const trigger =
      typeof triggerSelector === "string"
        ? document.querySelector(triggerSelector)
        : triggerSelector;
    const fileInput =
      typeof inputSelector === "string"
        ? document.querySelector(inputSelector)
        : inputSelector;
    const hiddenInput =
      typeof hiddenInputSelector === "string"
        ? document.querySelector(hiddenInputSelector)
        : hiddenInputSelector;

    if (!trigger || !fileInput || !hiddenInput) {
      console.error("[AiImageUploader] Required elements not found");
      return;
    }

    this.trigger = trigger;
    this.fileInput = fileInput;
    this.hiddenInput = hiddenInput;

    // 保存事件处理器引用（用于后续清理）
    this.triggerHandler = () => {
      console.log("[AiImageUploader] Trigger clicked!", trigger);

      if (!this.validateCustomFieldsBeforeUpload()) {
        return;
      }

      // 清除错误状态（红色边框）
      if (trigger) {
        trigger.classList.remove("validation-error");
      }

      // 双人模式：两张客户照片都选完后，才打开 AI 弹窗；点击可重试未完成的 AI 生成
      if (this.isCoupleMode()) {
        const section = this.getUploadSection();
        if (
          this.couplePhotosReady(section) &&
          this.hasPendingPhotoOnly()
        ) {
          this.maybeStartCoupleAiGeneration(section);
          return;
        }
      }

      // 清除状态文本（从触发器的ID推断状态元素ID）
      const triggerId = trigger.id;
      let statusId = "ai-upload-status";
      if (triggerId === "ai-upload-trigger-2") {
        statusId = "ai-upload-status-2";
      }
      const statusDiv = document.getElementById(statusId);
      if (statusDiv && !this.hasPendingPhotoOnly()) {
        statusDiv.textContent = "";
        statusDiv.className = "ai-upload-status";
      }
      
      // Track upload click event
      if (typeof GtagUtils !== 'undefined') {
        GtagUtils.trackUploadClick();
      }

      console.log("[AiImageUploader] Opening file input...", fileInput);
      fileInput.click();
    };
    this.fileInputHandler = (e) => {
      this.handleFileSelect(e);
    };

    // 绑定事件
    console.log(
      "[AiImageUploader Init] Binding click event to trigger:",
      trigger
    );
    trigger.addEventListener("click", this.triggerHandler);
    fileInput.addEventListener("change", this.fileInputHandler);
    console.log("[AiImageUploader Init] Events bound successfully!");
  }

  /**
   * Initialize properties-only generation (no customer photo upload).
   * @param {string|HTMLElement} triggerSelector - Generate button selector or element
   */
  initPropertiesOnly(triggerSelector) {
    const trigger =
      typeof triggerSelector === "string"
        ? document.querySelector(triggerSelector)
        : triggerSelector;

    if (!trigger || trigger.dataset.uploaderInitialized === "true") {
      return;
    }

    trigger.dataset.uploaderInitialized = "true";
    this.trigger = trigger;
    this.propertiesOnlyMode = true;

    this.triggerHandler = async () => {
      if (!this.validateCustomFieldsBeforeUpload()) {
        return;
      }

      trigger.classList.remove("validation-error");

      const status = this.getUploadSection()?.querySelector("#ai-generate-status");
      if (status) {
        status.textContent = "";
        status.className = "ai-upload-status";
      }

      if (typeof GtagUtils !== "undefined") {
        GtagUtils.trackUploadClick();
      }

      await this.startPropertiesOnlyGeneration();
    };

    trigger.addEventListener("click", this.triggerHandler);
  }

  async startPropertiesOnlyGeneration() {
    if (!this.validateCustomFieldsBeforeUpload()) {
      return;
    }

    if (!window.aiUploadModal) {
      setTimeout(() => this.startPropertiesOnlyGeneration(), 100);
      return;
    }

    await this.fetchSelectedOptions();
    await this.detectCharacterType();

    const defaultSourceType = this.config.sourceType;

    window.aiUploadModal.showForPropertiesGeneration(
      async (confirmedFile, sourceType) => {
        if (sourceType) {
          this.config.sourceType = sourceType;
        }
        await this.processUpload(null, { propertiesOnly: true });
      },
      async () => {
        await this.processUpload(null, {
          propertiesOnly: true,
          skipProgressInit: true,
        });
      },
      { sourceType: defaultSourceType }
    );
  }

  async isHeicFormat(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arr = new Uint8Array(e.target.result);
        // Check for HEIC/HEIF magic numbers
        // ftyp at offset 4, followed by heic/heix/hevc/hevx/mif1
        if (arr.length < 12) {
          resolve(false);
          return;
        }
        const header = String.fromCharCode(...arr.slice(4, 12));
        const isHeic =
          header.includes("ftyp") &&
          (header.includes("heic") ||
            header.includes("heix") ||
            header.includes("hevc") ||
            header.includes("hevx") ||
            header.includes("mif1"));
        resolve(isHeic);
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  }

  /**
   * 处理文件选择
   */
  async handleFileSelect(event) {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    if (!this.validateCustomFieldsBeforeUpload()) {
      event.target.value = "";
      return;
    }

    // 验证文件
    const validation = this.validateFile(file);
    if (!validation.valid) {
      if (typeof MessageToast !== "undefined") {
        MessageToast.error(validation.message);
      } else {
        MessageToast.error(validation.message);
      }
      event.target.value = "";
      return;
    }

    // 等待全局弹窗初始化
    if (!window.aiUploadModal) {
      setTimeout(() => event.target.dispatchEvent(new Event("change")), 100);
      return;
    }

    // 转换图片格式（HEIC/WEBP → JPG）- 在显示预览之前
    let convertedFile = file; // 使用新变量存储转换后的文件
    const fileName = file.name.toLowerCase();
    const isHeic =
      fileName.endsWith(".heic") ||
      fileName.endsWith(".heif") ||
      file.type === "image/heic" ||
      file.type === "image/heif";
    const isWebp = fileName.endsWith(".webp") || file.type === "image/webp";
    // Check HEIC format by file extension OR file header (magic number)
    const contentIsHeic = await this.isHeicFormat(file);

    if (isHeic || contentIsHeic) {
      try {
        convertedFile = await this.convertHeicToJpg(file);
      } catch (error) {
        console.error("[AiImageUploader] HEIC conversion error:", error);
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(error.message);
        } else {
          MessageToast.error(error.message);
        }
        event.target.value = "";
        return;
      }
    } else if (isWebp) {
      try {
        convertedFile = await this.convertWebpToJpg(file);
      } catch (error) {
        console.error("[AiImageUploader] WEBP conversion error:", error);
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(
            "Failed to convert WEBP image. Please use JPG or PNG format."
          );
        } else {
          MessageToast.error(
            "Failed to convert WEBP image. Please use JPG or PNG format."
          );
        }
        event.target.value = "";
        return;
      }
    }

    // 获取图片尺寸并检查是否需要转换为正方形
    try {
      const { width, height } = await this.getImageDimensions(convertedFile);
      console.log(`[AiImageUploader] Image dimensions: ${width}x${height}`);

      // 验证图片分辨率
      const minDim = this.config.minDimension;
      const maxDim = this.config.maxDimension;

      if (width < minDim || height < minDim) {
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(
            `Image resolution too small. Minimum ${minDim}x${minDim} pixels required. Your image: ${width}x${height}`
          );
        } else {
          alert(
            `Image resolution too small. Minimum ${minDim}x${minDim} pixels required. Your image: ${width}x${height}`
          );
        }
        event.target.value = "";
        return;
      }

      if (width > maxDim || height > maxDim) {
        if (typeof MessageToast !== "undefined") {
          MessageToast.error(
            `Image resolution too large. Maximum ${maxDim}x${maxDim} pixels allowed. Your image: ${width}x${height}`
          );
        } else {
          alert(
            `Image resolution too large. Maximum ${maxDim}x${maxDim} pixels allowed. Your image: ${width}x${height}`
          );
        }
        event.target.value = "";
        return;
      }

      console.log(
        `[AiImageUploader] ✅ Image resolution valid (${minDim}-${maxDim}px): ${width}x${height}`
      );

      console.log(
        "[AiImageUploader] Converting long image to square with white background"
      );
      convertedFile = await this.convertToSquare(convertedFile, width, height);
    } catch (error) {
      console.error(
        "[AiImageUploader] Failed to process image dimensions:",
        error
      );
      // 即使获取尺寸失败，也继续上传原图
    }

    // 双人模式：每次选图后检查是否两张都已选，全部就绪才打开 AI 弹窗
    if (this.isCoupleMode()) {
      const section = this.getUploadSection();
      const slot = this.getUploadSlotIndex();
      const pending = this.ensureCouplePendingFiles(section);
      pending[slot] = convertedFile;

      this.showLocalPhotoPreview(convertedFile);
      event.target.value = "";

      if (!this.couplePhotosReady(section)) {
        const missingSlot = pending[1] ? 2 : 1;
        section
          .querySelector(
            `.ai-upload-container[data-upload-index="${missingSlot}"]`
          )
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      await this.maybeStartCoupleAiGeneration(section);
      return;
    }

    event.target.value = "";
    await this.openGenerationModal(convertedFile);
  }

  getUploadSection() {
    return this.trigger?.closest(
      ".ai-upload-section, #ai-upload-section, #ai-image-upload-section, .ai-image-upload-section"
    );
  }

  getUploadSlotIndex() {
    return this.trigger?.id === "ai-upload-trigger-2" ? 2 : 1;
  }

  isCoupleMode() {
    return this.getUploadSection()?.dataset.coupleMode === "true";
  }

  ensureCouplePendingFiles(section) {
    if (!section._couplePendingFiles) {
      section._couplePendingFiles = { 1: null, 2: null };
    }
    return section._couplePendingFiles;
  }

  couplePhotosReady(section) {
    const pending = section?._couplePendingFiles || {};
    return Boolean(pending[1] && pending[2]);
  }

  async maybeStartCoupleAiGeneration(section) {
    if (!section || section.dataset.coupleMode !== "true") return;
    if (!this.couplePhotosReady(section)) return;

    const aiHidden = section.querySelector("#ai-custom-image-url");
    if (aiHidden?.value?.trim()) return;

    const pending = section._couplePendingFiles;
    await section._aiUploaders?.[1]?.openGenerationModal(pending[1]);
  }

  hasPendingPhotoOnly() {
    const section = this.getUploadSection();
    const pending = section?._couplePendingFiles;
    const aiHidden = section?.querySelector("#ai-custom-image-url");
    const hasAiUrl = Boolean(aiHidden?.value?.trim());
    return Boolean(
      this.isCoupleMode() && pending?.[1] && pending?.[2] && !hasAiUrl
    );
  }

  showLocalPhotoPreview(file) {
    const preview =
      this.trigger?.querySelector(".ai-upload-preview") ||
      document.getElementById(
        this.getUploadSlotIndex() === 2
          ? "ai-upload-preview-2"
          : "ai-upload-preview"
      );

    if (!preview) return;

    if (this._localPreviewUrl) {
      URL.revokeObjectURL(this._localPreviewUrl);
    }

    this._localPreviewUrl = URL.createObjectURL(file);
    preview.src = this._localPreviewUrl;
    this.trigger.classList.add("has-image");
    this.trigger.classList.remove("validation-error");
    this.trigger.dataset.pendingPhoto = "true";
  }

  updateCoupleStatus(message, isSuccess = false) {
    const statusId =
      this.getUploadSlotIndex() === 2
        ? "ai-upload-status-2"
        : "ai-upload-status";
    const statusDiv = document.getElementById(statusId);
    if (!statusDiv) return;

    statusDiv.textContent = message;
    statusDiv.className = isSuccess
      ? "ai-upload-status success"
      : "ai-upload-status";
  }

  async openGenerationModal(convertedFile) {
    await this.fetchSelectedOptions();
    await this.detectCharacterType();

    const section = this.getUploadSection();
    const pending = section?._couplePendingFiles;
    const preview1Src = section?.querySelector("#ai-upload-preview")?.src || "";
    const preview2Src =
      section?.querySelector("#ai-upload-preview-2")?.src || "";
    const modalOptions =
      this.isCoupleMode() && pending?.[1] && pending?.[2]
        ? {
            coupleFiles: [pending[1], pending[2]],
            couplePreviewUrls: [preview1Src, preview2Src],
          }
        : undefined;

    window.aiUploadModal.show(
      convertedFile,
      async (confirmedFile, sourceType) => {
        if (sourceType) {
          this.config.sourceType = sourceType;
        }
        await this.processUpload(confirmedFile, this.buildUploadOptions(section));
      },
      async (originalFile) => {
        await this.processUpload(
          originalFile,
          this.buildUploadOptions(section, { skipProgressInit: true })
        );
      },
      modalOptions
    );
  }

  buildUploadOptions(section, extra = {}) {
    const pending = section?._couplePendingFiles;
    const coupleReady =
      this.isCoupleMode() && pending?.[1] && pending?.[2];

    return {
      ...extra,
      secondFile: coupleReady ? pending[2] : null,
      coupleMode: Boolean(coupleReady),
    };
  }

  /**
   * 获取当前选中的产品选项（尺寸和风格）
   */
  async fetchSelectedOptions() {
    const root = this.getCurrentContainer();
    const productForm =
      root.querySelector('form[id*="product-form"]') ||
      root.querySelector('form[id^="BuyButtons-ProductForm"]') ||
      document.querySelector('form[id^="BuyButtons-ProductForm"]');
    const variantIdInput = productForm?.querySelector('input[name="id"]');

    if (!variantIdInput) {
      return;
    }

    const variantId = variantIdInput.value;
    const pfc = root.querySelector("product-form-component[data-product-url], cm-product-form[data-product-url]") ||
      document.querySelector("product-form-component[data-product-url], cm-product-form[data-product-url]");
    const fromDataset = pfc?.dataset?.productUrl
      ?.replace(/^\/products\//, "")
      .split("?")[0];
    const productHandle =
      fromDataset ||
      window.location.pathname.split("/products/")[1]?.split("?")[0];

    if (!productHandle) {
      return;
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();
      const currentVariant = productData.variants.find(
        (v) => v.id == variantId
      );

      if (currentVariant) {
        const selectedSize = currentVariant.option1;
        const selectedStyle = currentVariant.option2;

        // 保存到全局变量
        window.selectedProductOptions = {
          size: selectedSize,
          style: selectedStyle,
          variantId: variantId,
          productHandle: productHandle,
        };
      }
    } catch (err) {
      console.error("[AiImageUploader] Failed to fetch product options:", err);
    }
  }

  /**
   * 验证文件
   */
  validateFile(file) {
    // 检查文件类型 - 通过 MIME 类型或文件扩展名
    const fileName = file.name.toLowerCase();
    const isValidType =
      this.config.acceptFileTypes.includes(file.type) ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png") ||
      fileName.endsWith(".webp") ||
      fileName.endsWith(".heic") ||
      fileName.endsWith(".heif");

    if (!isValidType) {
      return {
        valid: false,
        message: "Please select an image file (JPEG, JPG, PNG, WEBP, HEIC)",
      };
    }

    // 检查文件大小
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > this.config.maxFileSizeMB) {
      return {
        valid: false,
        message: `File size exceeds ${this.config.maxFileSizeMB}MB limit`,
      };
    }

    return { valid: true };
  }

  /**
   * 将 HEIC 图片转换为 JPG
   */
  async convertHeicToJpg(file) {
    try {
      // 使用 heic-to 库转换 HEIC 到 JPEG
      if (typeof HeicTo === "undefined") {
        await this.loadHeicTo();
      }

      // 检查是否是 HEIC 文件
      const isHeic = await HeicTo.isHeic(file);
      if (!isHeic) {
        return file;
      }

      // 转换为 JPEG
      const jpegBlob = await HeicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.95,
      });

      const jpgFile = new File(
        [jpegBlob],
        file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"),
        { type: "image/jpeg" }
      );

      return jpgFile;
    } catch (error) {
      console.error("[AiImageUploader] HEIC to JPG conversion failed:", error);
      throw new Error(
        "Failed to convert HEIC image. Please use JPG or PNG format."
      );
    }
  }

  /**
   * 动态加载 heic-to 库
   */
  async loadHeicTo() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/heic-to@1.3.0/dist/iife/heic-to.js";
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error("Failed to load heic-to library"));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * 将 WEBP 图片转换为 JPG
   */
  async convertWebpToJpg(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const jpgFile = new File(
                  [blob],
                  file.name.replace(/\.webp$/i, ".jpg"),
                  { type: "image/jpeg" }
                );
                resolve(jpgFile);
              } else {
                reject(new Error("Failed to convert image to JPG"));
              }
            },
            "image/jpeg",
            0.95
          );
        };

        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  /**
   * 获取图片尺寸
   * @param {File} file - 图片文件
   * @returns {Promise<{width: number, height: number}>}
   */
  async getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Use createObjectURL for better reliability
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const result = { width: img.width, height: img.height };
        URL.revokeObjectURL(objectUrl); // Clean up
        resolve(result);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl); // Clean up
        reject(new Error("Failed to load image"));
      };

      img.src = objectUrl;
    });
  }

  /**
   * 将长图转换为正方形（添加白色背景填充）
   * @param {File} file - 图片文件
   * @param {number} width - 图片宽度
   * @param {number} height - 图片高度
   * @returns {Promise<File>}
   */
  async convertToSquare(file, width, height) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          // 计算正方形边长（取长边）
          const size = Math.max(width, height);

          // 创建正方形 canvas
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;

          const ctx = canvas.getContext("2d");

          // 填充白色背景
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, size, size);

          // 计算图片居中位置
          const x = (size - width) / 2;
          const y = (size - height) / 2;

          // 绘制图片（居中）
          ctx.drawImage(img, x, y, width, height);

          // 转换为 Blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const squareFile = new File(
                  [blob],
                  file.name.replace(/\.\w+$/, "_square.jpg"),
                  { type: "image/jpeg" }
                );
                console.log(
                  `[AiImageUploader] Converted to square: ${width}x${height} -> ${size}x${size}`
                );
                resolve(squareFile);
              } else {
                reject(new Error("Failed to convert to square"));
              }
            },
            "image/jpeg",
            0.95
          );
        };

        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  }

  /**
   * 压缩图片（带目标大小限制）
   * @param {File} file - 原始图片文件
   * @param {number} targetKB - 目标文件大小（KB），如果不指定则使用默认压缩
   * @returns {Promise<File>} 压缩后的图片文件
   */
  async compressImageWithLimit(file, targetKB = null) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = async () => {
        const { width, height } = img;

        // 创建 canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // 如果图片尺寸过大，先缩小尺寸
        let newWidth = width;
        let newHeight = height;
        const maxSide = Math.max(width, height);
        if (maxSide > 2048) {
          newWidth = Math.floor(width / 2);
          newHeight = Math.floor(height / 2);
        }

        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // 如果没有指定目标大小，使用默认质量压缩
        if (!targetKB) {
          canvas.toBlob(
            (blob) =>
              resolve(
                new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
                  type: "image/jpeg",
                })
              ),
            "image/jpeg",
            0.8
          );
          return;
        }

        // 二分查找最佳压缩质量，使文件大小接近目标值
        let minQ = 0.1;
        let maxQ = 0.95;
        let quality = 0.8;
        const targetBytes = targetKB * 1024;
        let blob = null;

        // 最多迭代10次寻找最佳质量
        for (let i = 0; i < 10; i++) {
          blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", quality)
          );

          if (blob.size <= targetBytes) {
            minQ = quality;
            quality = (quality + maxQ) / 2;
          } else {
            maxQ = quality;
            quality = (quality + minQ) / 2;
          }
        }

        const compressedFile = new File(
          [blob],
          file.name.replace(/\.\w+$/, ".jpg"),
          {
            type: "image/jpeg",
          }
        );

        const originalSizeKB = (file.size / 1024).toFixed(2);
        const compressedSizeKB = (compressedFile.size / 1024).toFixed(2);
        const reductionPercent = (
          ((file.size - compressedFile.size) / file.size) *
          100
        ).toFixed(1);

        console.log(
          `[AiImageUploader] Compressed image from ${originalSizeKB} KB to ${compressedSizeKB} KB (${reductionPercent}% reduction)`
        );

        resolve(compressedFile);
      };

      img.onerror = () => {
        resolve(file); // 如果加载失败，返回原文件
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 处理上传流程
   * @param {File} file - 要上传的文件
   * @param {boolean|object} [uploadOptions=false] - 跳过进度初始化或上传选项
   */
  async processUpload(file, uploadOptions = false) {
    const options =
      typeof uploadOptions === "object" && uploadOptions !== null
        ? uploadOptions
        : { skipProgressInit: uploadOptions === true };
    const propertiesOnly = Boolean(options.propertiesOnly);
    const skipProgressInit = Boolean(options.skipProgressInit);
    const secondFile = propertiesOnly ? null : options.secondFile || null;
    const coupleMode = propertiesOnly ? false : Boolean(options.coupleMode);

    if (!propertiesOnly && !file) {
      return;
    }

    if (!this.validateCustomFieldsBeforeUpload()) {
      return;
    }

    try {
      // Track upload count for order_placed analytics
      if (typeof GtagTrackingHelpers !== 'undefined') {
        GtagTrackingHelpers.incrementUploadCount();
      }

      // 检查并锁定抽屉，防止在上传过程中被关闭
      this._setUploadInProgress(true);

      let compressedSecondFile = null;
      if (!propertiesOnly) {
        // 压缩图片到 200KB（在上传前）
        file = await this.compressImageWithLimit(file, 200);
        if (secondFile) {
          compressedSecondFile = await this.compressImageWithLimit(secondFile, 200);
        }

        // 回调: 上传开始
        if (this.config.onUploadStart) {
          this.config.onUploadStart(file);
        }
      }

      // 立即设置图片选择回调（在轮询开始前）
      // 这样用户选择图片后点击确认就会直接关闭，不会重新触发上传
      window.aiUploadModal.confirmCallback = () => {
        const selectedUrl = window.aiUploadModal.getSelectedImageUrl();
        if (selectedUrl) {
          // 停止轮询（如果还在进行中）
          if (window.aiUploadModal.abortController) {
            window.aiUploadModal.abortController.abort();
          }

          // 停止进度动画
          if (this.progressAnimation) {
            this.progressAnimation.stop();
          }

          this.saveImageUrl(selectedUrl);

          // 解锁抽屉
          this._setUploadInProgress(false);

          window.aiUploadModal.close();

          // 回调: 图片选择（弹窗关闭后再同步轮播并滚动到预览区）
          if (this.config.onImageSelect) {
            this.config.onImageSelect(selectedUrl);
          }
        }
      };

      // 显示进度条（除非是重新生成，进度条已由 regenerate() 初始化）
      if (!skipProgressInit) {
        window.aiUploadModal.showProgress();

      // 根据 sourceType 设置进度条时长
      // Quick Generate: 27秒, Better Generate: 55秒
      const progressDuration =
        this.config.sourceType === "cute_single_img2img_deliver3d"
          ? 27000
          : 55000;
        this.progressAnimation =
          window.aiUploadModal.startProgressAnimation(progressDuration);
      }

      const adapter =
        typeof window.getAiUploadApiAdapter === "function"
          ? window.getAiUploadApiAdapter(this)
          : null;

      if (adapter) {
        const taskId = await adapter.upload(propertiesOnly ? null : file, {
          secondFile: compressedSecondFile,
          coupleMode,
          propertiesOnly,
        });
        if (taskId) {
          this.saveTaskId(taskId);
        }
        if (adapter.userImageUrls?.length) {
          this.saveUserImageUrls(adapter.userImageUrls);
        }
        const pollResult = await adapter.poll(this);
        const userImageUrls = Array.isArray(pollResult?.userImageUrls)
          ? pollResult.userImageUrls
          : adapter.userImageUrls;
        if (userImageUrls?.length) {
          this.saveUserImageUrls(userImageUrls);
        }
      } else {
        await this.uploadImage(file);
        await this.pollImages();
      }

      const generatedImages = [];

      // 停止进度动画
      if (this.progressAnimation) {
        this.progressAnimation.stop();
      }

      // 回调: 上传成功
      if (this.config.onUploadSuccess) {
        this.config.onUploadSuccess(generatedImages);
      }
    } catch (error) {
      // 用户取消
      if (error.name === "AbortError") {
        console.log("[AiImageUploader] Upload cancelled by user");
        // 解锁抽屉
        this._setUploadInProgress(false);
        return;
      }

      console.error("[AiImageUploader] Upload error:", error);

      // 停止进度动画
      if (this.progressAnimation) {
        this.progressAnimation.stop();
      }

      // 显示友好的错误提示
      if (error.isAllFailed) {
        // 全部失败错误：已经在 pollImages 中显示过了，不需要再次显示
        console.log("[AiImageUploader] All images failed, error already shown");
      } else if (error.isDailyLimit) {
        window.aiUploadModal.showError(
          "Daily Limit Reached",
          error.message ||
            "You've reached today's limit of 10 AI previews. Please try again tomorrow."
        );
      } else if (error.isRateLimit) {
        // 限频错误：显示特殊提示
        window.aiUploadModal.showError(
          "General error",
          "Please wait a moment and try again."
        );
      } else if (this.isNetworkError(error)) {
        // 网络错误：显示网络提示
        window.aiUploadModal.showError(
          "Network Error",
          "Poor network connection. Please try again later.",
          null
        );
      } else {
        // 其他错误：显示通用错误提示
        window.aiUploadModal.showError(
          "Upload Failed",
          error.message ||
            "An error occurred while uploading your image. Please try again.",
          null
        );
      }

      // 解锁抽屉
      this._setUploadInProgress(false);

      // 回调: 上传错误
      if (this.config.onUploadError) {
        this.config.onUploadError(error);
      }

      // 不关闭弹窗，让用户看到错误信息
      // window.aiUploadModal.close();
    }
  }

  /**
   * Validate required custom fields before opening upload or starting generation.
   * @returns {boolean}
   */
  validateCustomFieldsBeforeUpload() {
    if (typeof window.ProductCustomFields?.guard !== "function") {
      return true;
    }

    return window.ProductCustomFields.guard(this.getCurrentContainer());
  }

  /**
   * 获取当前活跃的产品容器（抽屉/popover/主页面）
   * @returns {HTMLElement} 当前活跃的容器元素
   */
  getCurrentContainer() {
    const quickAddDialog = document.querySelector("quick-add-dialog dialog[open]");
    if (quickAddDialog) {
      return (
        quickAddDialog.querySelector("#quick-add-modal-content") ||
        quickAddDialog
      );
    }

    const openDrawer = document.querySelector("quick-buy-drawer[open]");
    if (openDrawer) {
      return openDrawer;
    }

    const openPopover = document.querySelector("quick-buy-popover[open]");
    if (openPopover) {
      return openPopover;
    }

    return (
      document.querySelector(".product-information") ||
      document.querySelector(".main-product") ||
      document.body
    );
  }

  /**
   * 检测角色类型（pet、person、couple、human-pet 或 keychain）
   * @returns {string} 'pet'、'person'、'couple'、'human-pet' 或 'keychain'
   */
  async detectCharacterType() {
    let characterType = "person"; // 默认值

    // 获取当前活跃的容器
    const container = this.getCurrentContainer();

    // 0. 获取商品名称
    let titleText = "";
    const titleSelectors = [
      ".product-item-meta__title",
      ".product__title",
      ".product-meta__title",
      "h1",
      '[class*="title"]',
    ];

    for (const selector of titleSelectors) {
      const productTitle = container.querySelector(selector);
      if (productTitle && productTitle.textContent.trim()) {
        titleText = productTitle.textContent.trim();
        break;
      }
    }

    // 检查标题是否包含 "1 person + 1 pet"
    const hasHumanPetInTitle = /1\s*person\s*\+\s*1\s*pet/i.test(titleText);
    const hasPetInTitle = /\bpet\b/i.test(titleText);

    // 1. 检查当前选择的变体选项（在当前容器内查找）
    const allSelectedOptions = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked'
    );
    let hasPetInOption = false;
    let hasCoupleInOption = false;
    let hasHumanPetInOption = false;

    allSelectedOptions.forEach((radio) => {
      const optionValue = radio.value || "";
      const optionText = radio.getAttribute("data-option-value") || optionValue;

      // 检查是否包含 "1 person + 1 pet"
      if (/1\s*person\s*\+\s*1\s*pet/i.test(optionText)) {
        hasHumanPetInOption = true;
      }

      // 检查是否包含 "pet"
      if (/\bpet\b/i.test(optionText)) {
        hasPetInOption = true;
      }

      // 检查是否包含 "2 people (connected)" 或类似的 couple 选项
      if (
        /2\s*people\s*\(connected\)/i.test(optionText) ||
        /\bcouple\b/i.test(optionText)
      ) {
        hasCoupleInOption = true;
      }
    });

    // 2. 如果标题或选项包含 "1 person + 1 pet"，返回 human-pet
    if (hasHumanPetInTitle || hasHumanPetInOption) {
      return "human-pet";
    }

    // 3. 如果标题或选项包含 pet，返回 pet
    if (hasPetInTitle || hasPetInOption) {
      return "pet";
    }

    // 4. 如果选项包含 "2 people (connected)" 或 "couple"，返回 couple
    if (hasCoupleInOption) {
      return "couple";
    }

    // 5. 检查商品标签是否包含 "Personalized Figure Keychains"
    // const productForm = container.querySelector('form[is="product-form"]');
    // const productTagsString = productForm?.getAttribute('data-product-tags') || '';

    // if (productTagsString.includes('personalized figure keychains')) {
    //   return 'keychain';
    // }

    // 6. 默认返回 person
    return characterType;
  }

  _setUploadInProgress(inProgress) {
    const flag = inProgress ? "true" : "false";
    document
      .querySelectorAll(
        "quick-add-dialog, quick-buy-drawer[open], quick-buy-popover[open]"
      )
      .forEach((el) => {
        el.dataset.aiUploadInProgress = flag;
      });
  }

  /**
   * 上传图片到后端 (live API)
   */
  async uploadImage(file) {
    // 从全局变量获取用户选择的 size
    const selectedOptions = window.selectedProductOptions || {};
    const selectedSize = selectedOptions.size || "";

    // 使用 getCurrentStyle() 获取当前选中的风格（已经映射为 API 格式）
    const apiStyleType = this.getCurrentStyle();

    // 检测角色类型
    const characterType = await this.detectCharacterType();

    const formData = new FormData();
    formData.append("images", file);
    formData.append("type", this.config.sourceType);
    formData.append("QImageNum", "1");
    formData.append("jobNum", this.config.jobNum);
    formData.append("styleType", apiStyleType);
    formData.append("sourcePage", "deliver3D");
    formData.append("fromSource", "deliver3D");
    formData.append("characterType", characterType); // 总是添加 characterType

    const authToken = this.getAuthToken();
    if (!authToken) {
      throw new Error("No authentication token. Please refresh the page.");
    }

    const signal = window.aiUploadModal.abortController?.signal;

    const response = await fetch(
      this.config.apiBaseUrl + this.config.uploadEndpoint,
      {
        method: "POST",
        headers: { Authorization: authToken },
        body: formData,
        signal: signal,
      }
    );

    const data = await response.json();

    if (response.status === 403) {
      const dailyLimitError = new Error(
        "You've reached today's limit of 10 AI previews. Please try again tomorrow."
      );
      dailyLimitError.isDailyLimit = true;
      throw dailyLimitError;
    }

    // 检查是否为限频错误
    if (data.success === false && data.failures && data.failures.length > 0) {
      const failure = data.failures[0];

      // 检查是否包含限频相关的错误信息
      if (failure.error && failure.error.includes("exceeds the limit")) {
        const rateLimitError = new Error("Please wait a moment and try again.");
        rateLimitError.isRateLimit = true;
        rateLimitError.originalMessage = failure.error;
        throw rateLimitError;
      }

      // 其他失败错误
      throw new Error(failure.error || "Upload failed");
    }

    if (!data.success || !data.results?.[0]?.uuids) {
      throw new Error("Invalid response from server");
    }

    if (!response.ok) {
      throw new Error("Upload failed: " + response.statusText);
    }

    return data.results[0].uuids;
  }

  /**
   * 轮询获取生成的图片 (live API)
   */
  async pollImages() {
    let attempts = 0;
    let completedImages = new Set();
    let hasInitializedGrid = false;

    while (attempts < this.config.maxPollAttempts) {
      attempts++;

      // 检查是否被取消
      if (window.aiUploadModal.abortController?.signal.aborted) {
        throw new DOMException("Upload cancelled", "AbortError");
      }

      if (this.config.onUploadProgress) {
        this.config.onUploadProgress(attempts, this.config.maxPollAttempts);
      }

      try {
        const authToken = this.getAuthToken();
        if (!authToken) throw new Error("No authentication token");

        const signal = window.aiUploadModal.abortController?.signal;

        const response = await fetch(
          this.config.apiBaseUrl +
            this.config.pollEndpoint +
            "?page=1&pageSize=1&taskType=all",
          {
            method: "GET",
            headers: { Authorization: authToken },
            signal: signal,
          }
        );

        if (!response.ok) {
          // 如果是 HTTP 错误，停止轮询
          if (response.status >= 500) {
            throw new Error("Server error: " + response.statusText);
          }
          await this.sleep(this.config.pollInterval);
          continue;
        }

        const result = await response.json();

        if (!result.success || !result.data?.length) {
          await this.sleep(this.config.pollInterval);
          continue;
        }

        const latestTask = result.data[0];
        const batches = latestTask.batches || [];

        if (!batches.length) {
          await this.sleep(this.config.pollInterval);
          continue;
        }

        // Status constants
        const STATUS = {
          COMPLETED: "completed",
          COMPLETE: "complete",
          PENDING: "pending",
          FAILED: "failed",
          WAITING: "waiting",
          PROCESSING: "processing",
          QUEUED: "queued",
        };

        // Process each batch (use jobId as unique identifier to prevent duplicate processing)
        batches.forEach((batch, index) => {
          const status = batch.status?.toLowerCase();
          const batchId = batch.jobId || index; // Use jobId as unique identifier

          // Skip if already processed (keyed by jobId, not array index)
          if (completedImages.has(batchId)) return;

          // Show completed images
          if (status === STATUS.COMPLETED || status === STATUS.COMPLETE) {
            const url = batch.batchData?.fileKeyImg0 || "";
            if (url) {
              // 当收到第一张成功图片时，才初始化图片选择网格
              if (!hasInitializedGrid) {
                window.aiUploadModal.initImageSelection();
                hasInitializedGrid = true;
              }
              window.aiUploadModal.updateImageSlot(index, url);
              completedImages.add(batchId);
            }
          }
          // Show failed state
          else if (status === STATUS.FAILED) {
            window.aiUploadModal.updateImageSlotFailed(index);
            // 如果网格还未初始化，但已有失败结果，也初始化网格以显示失败状态
            if (!hasInitializedGrid) {
              window.aiUploadModal.initImageSelection();
              hasInitializedGrid = true;
              window.aiUploadModal.updateImageSlotFailed(index); // 再次调用以确保UI正确
            }
            completedImages.add(batchId);
          }
          // Show loading state for pending/queued/processing
          else if (
            status === STATUS.QUEUED ||
            status === STATUS.PENDING ||
            status === STATUS.PROCESSING ||
            status === STATUS.WAITING
          ) {
            // Keep showing loading spinner (already initialized)
          }
        });

        // Check if all batches are in terminal state (completed or failed)
        const allTerminal = batches.every((b) => {
          const status = b.status?.toLowerCase();
          return (
            status === STATUS.COMPLETED ||
            status === STATUS.COMPLETE ||
            status === STATUS.FAILED
          );
        });

        // If all failed, show error and stop
        const allFailed = batches.every(
          (b) => b.status?.toLowerCase() === STATUS.FAILED
        );
        if (allFailed) {
          window.aiUploadModal.showError(
            "",
            "Please try again with a different photo."
          );
          const error = new Error("All images generation failed");
          error.isAllFailed = true; // 标记为全部失败错误
          throw error;
        }

        // If all are in terminal state, return results
        if (allTerminal && completedImages.size === batches.length) {
          completedImages.clear();
          return batches
            .map((b) => ({
              jobId: b.jobId,
              url: b.batchData?.fileKeyImg0 || "",
              status: b.status,
            }))
            .filter((img) => img.url); // Only return successful ones
        }

        await this.sleep(this.config.pollInterval);
      } catch (error) {
        // If it's a cancel error, throw it directly
        if (error.name === "AbortError") {
          throw error;
        }

        // If all images failed, stop polling immediately
        if (error.isAllFailed) {
          throw error;
        }

        // If it's a critical error, stop polling
        if (
          error.message.includes("Server error") ||
          error.message.includes("authentication")
        ) {
          throw error;
        }

        await this.sleep(this.config.pollInterval);
      }
    }

    throw new Error("Processing timeout - please try again");
  }

  /**
   * 获取当前选中的风格
   */
  getCurrentStyle() {
    // 获取当前活跃的容器
    const container = this.getCurrentContainer();

    // 方法1: 从全局变量获取（优先）
    const selectedOptions = window.selectedProductOptions;
    if (selectedOptions && selectedOptions.style) {
      const mappedStyle = this.styleMapping[selectedOptions.style];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // 方法2: 从当前容器中查找所有选中的 radio
    const allCheckedRadios = container.querySelectorAll(
      'input[type="radio"][name^="option"]:checked'
    );

    // 智能查找风格选项：检查值是否在映射表中
    for (const radio of allCheckedRadios) {
      const value = radio.value;
      const mappedStyle = this.styleMapping[value];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // 方法3: 从当前容器中的显示值获取（降级）
    const allValueDisplays = container.querySelectorAll(
      '[id*="option"][id*="-value"]'
    );
    for (const display of allValueDisplays) {
      const value = display.textContent.trim();
      const mappedStyle = this.styleMapping[value];
      if (mappedStyle) {
        return mappedStyle;
      }
    }

    // 方法4: 从当前容器中的选项选择器获取（最终降级）
    const allOptionSelectors = container.querySelectorAll(
      ".product-form__option-selector"
    );
    for (let index = 0; index < allOptionSelectors.length; index++) {
      const selector = allOptionSelectors[index];
      const checkedInput = selector.querySelector("input:checked");

      if (checkedInput) {
        const mappedStyle = this.styleMapping[checkedInput.value];
        if (mappedStyle) {
          return mappedStyle;
        }
      }
    }

    return this.config.defaultStyle;
  }

  /**
   * 保存 AI 生成图 URL
   */
  saveImageUrl(url) {
    const section = this.getUploadSection();
    const aiHidden =
      section?.querySelector("#ai-custom-image-url") || this.hiddenInput;

    if (aiHidden) {
      aiHidden.value = url;
    } else {
      console.error(
        "[AiImageUploader] AI hidden input not found - cannot save image URL"
      );
    }

    if (this.trigger) {
      delete this.trigger.dataset.pendingPhoto;
    }
  }

  /**
   * 保存生成任务 ID（加购 line item properties）
   */
  saveTaskId(taskId) {
    const section = this.getUploadSection();
    const taskHidden = section?.querySelector("#ai-task-id");
    if (taskHidden && taskId) {
      taskHidden.value = String(taskId);
    }
  }

  /**
   * 保存用户上传原图 URL（加购 line item properties）
   */
  saveUserImageUrls(urls) {
    const section = this.getUploadSection();
    if (!section || !Array.isArray(urls) || !urls.length) return;

    const userHidden1 = section.querySelector("#ai-user-image-url");
    const userHidden2 = section.querySelector("#ai-user-image-url-2");

    if (urls[0] && userHidden1) {
      userHidden1.value = urls[0];
    }
    if (urls[1] && userHidden2) {
      userHidden2.value = urls[1];
    }
  }

  /**
   * 判断是否为网络错误
   */
  isNetworkError(error) {
    if (!error) return false;

    const errorMessage = error.message?.toLowerCase() || "";
    const errorName = error.name?.toLowerCase() || "";

    // 常见的网络错误特征
    const networkErrorPatterns = [
      "failed to fetch", // fetch 失败
      "network request failed", // 网络请求失败
      "networkerror", // 网络错误
      "network error", // 网络错误
      "timeout", // 超时
      "connection", // 连接问题
      "unreachable", // 无法访问
      "offline", // 离线
      "dns", // DNS 问题
      "enotfound", // DNS 未找到
      "econnrefused", // 连接被拒绝
      "econnreset", // 连接重置
      "etimedout", // 超时
    ];

    // 检查错误消息是否包含网络错误关键词
    const hasNetworkPattern = networkErrorPatterns.some((pattern) =>
      errorMessage.includes(pattern)
    );

    // 检查是否为 TypeError (通常是 fetch 失败)
    const isTypeError =
      errorName === "typeerror" && errorMessage.includes("fetch");

    return hasNetworkPattern || isTypeError;
  }

  /**
   * 获取认证 Token
   */
  getAuthToken() {
    if ((window.AI_UPLOAD_CONFIG?.mode || "mock") === "mock") {
      return "Bearer mock-token";
    }
    const storageKey =
      window.AI_UPLOAD_CONFIG?.tokenStorageKey ||
      this.config.tokenStorageKey ||
      "tinypresent_guest_token";
    const token =
      localStorage.getItem(storageKey) || window.guestToken;
    return token ? "Bearer " + token : null;
  }

  /**
   * 延迟函数
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 取消上传
   */
  cancel() {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (this.progressAnimation) {
      this.progressAnimation.stop();
    }
  }

  /**
   * 销毁实例
   */
  destroy() {
    this.cancel();

    // 清理事件监听器
    if (this.trigger && this.triggerHandler) {
      this.trigger.removeEventListener("click", this.triggerHandler);
    }
    if (this.fileInput && this.fileInputHandler) {
      this.fileInput.removeEventListener("change", this.fileInputHandler);
    }

    // 清空引用
    this.trigger = null;
    this.fileInput = null;
    this.hiddenInput = null;
    this.abortController = null;
    this.progressAnimation = null;
  }
}

// 导出到全局
window.AiImageUploader = AiImageUploader;
